import React, { useState, useEffect } from 'react';
import { Cloud, Sun, CloudRain, CloudSnow, Wind, Droplets, Thermometer } from 'lucide-react';

// Crystal recommendations based on weather
const weatherCrystals = {
  sunny: { crystal: 'Citrine', reason: 'Amplifies the sun\'s golden energy', icon: '☀️' },
  cloudy: { crystal: 'Selenite', reason: 'Brings clarity through clouded skies', icon: '☁️' },
  rainy: { crystal: 'Aquamarine', reason: 'Harmonizes with water energy', icon: '🌧️' },
  stormy: { crystal: 'Black Tourmaline', reason: 'Protection during turbulent energy', icon: '⛈️' },
  snowy: { crystal: 'Clear Quartz', reason: 'Purification like fresh snow', icon: '❄️' },
  windy: { crystal: 'Blue Lace Agate', reason: 'Calms the restless air element', icon: '💨' },
  cold: { crystal: 'Carnelian', reason: 'Warms the spirit and body', icon: '🥶' },
  hot: { crystal: 'Moonstone', reason: 'Cooling lunar energy', icon: '🌡️' },
};

// Simulated weather data for demo (would use real API in production)
const getSimulatedWeather = () => {
  const conditions = ['sunny', 'cloudy', 'rainy', 'sunny', 'cloudy'];
  const now = new Date();
  const condition = conditions[now.getHours() % conditions.length];
  
  // Simulate temperature based on time of day
  const baseTemp = 68;
  const hourVariation = Math.sin((now.getHours() - 6) * Math.PI / 12) * 15;
  const temp = Math.round(baseTemp + hourVariation);
  
  return {
    condition,
    temperature: temp,
    humidity: 45 + Math.floor(Math.random() * 30),
    windSpeed: 5 + Math.floor(Math.random() * 15),
    location: 'Los Angeles, CA',
    feelsLike: temp + Math.floor(Math.random() * 5) - 2,
  };
};

const WeatherWidget = () => {
  const [weather, setWeather] = useState(null);
  const [crystalRec, setCrystalRec] = useState(null);

  useEffect(() => {
    // Get simulated weather
    const weatherData = getSimulatedWeather();
    setWeather(weatherData);
    
    // Get crystal recommendation
    let rec = weatherCrystals[weatherData.condition] || weatherCrystals.sunny;
    if (weatherData.temperature < 50) {
      rec = weatherCrystals.cold;
    } else if (weatherData.temperature > 85) {
      rec = weatherCrystals.hot;
    }
    setCrystalRec(rec);

    // Update every 30 minutes
    const interval = setInterval(() => {
      const newWeather = getSimulatedWeather();
      setWeather(newWeather);
    }, 1800000);

    return () => clearInterval(interval);
  }, []);

  if (!weather) return null;

  const WeatherIcon = () => {
    switch (weather.condition) {
      case 'sunny': return <Sun className="w-12 h-12 text-[#FFB800]" />;
      case 'cloudy': return <Cloud className="w-12 h-12 text-[#7A7A9A]" />;
      case 'rainy': return <CloudRain className="w-12 h-12 text-[#00B4D8]" />;
      case 'snowy': return <CloudSnow className="w-12 h-12 text-[#E8E8E8]" />;
      default: return <Sun className="w-12 h-12 text-[#FFB800]" />;
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#00B4D8]/10 via-white to-[#FFB800]/10 rounded-2xl p-6 border border-[#e8e4de]" data-testid="weather-widget">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Cloud className="w-5 h-5 text-[#00B4D8]" />
          <h3 className="font-serif text-lg text-[#1e1919]">Weather & Energy</h3>
        </div>
        <span className="text-xs text-[#7A7A9A]">{weather.location}</span>
      </div>

      {/* Main Weather Display */}
      <div className="flex items-center gap-6 mb-6">
        <WeatherIcon />
        <div>
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-bold text-[#1e1919]">{weather.temperature}</span>
            <span className="text-lg text-[#7A7A9A]">°F</span>
          </div>
          <p className="text-sm text-[#7A7A9A] capitalize">{weather.condition}</p>
        </div>
      </div>

      {/* Weather Details */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-white/50 rounded-xl p-3 text-center">
          <Thermometer className="w-4 h-4 text-[#FF6B9D] mx-auto mb-1" />
          <p className="text-xs text-[#7A7A9A]">Feels Like</p>
          <p className="text-sm font-semibold text-[#1e1919]">{weather.feelsLike}°F</p>
        </div>
        <div className="bg-white/50 rounded-xl p-3 text-center">
          <Droplets className="w-4 h-4 text-[#00B4D8] mx-auto mb-1" />
          <p className="text-xs text-[#7A7A9A]">Humidity</p>
          <p className="text-sm font-semibold text-[#1e1919]">{weather.humidity}%</p>
        </div>
        <div className="bg-white/50 rounded-xl p-3 text-center">
          <Wind className="w-4 h-4 text-[#9D4EDD] mx-auto mb-1" />
          <p className="text-xs text-[#7A7A9A]">Wind</p>
          <p className="text-sm font-semibold text-[#1e1919]">{weather.windSpeed} mph</p>
        </div>
      </div>

      {/* Crystal Recommendation */}
      {crystalRec && (
        <div className="bg-gradient-to-r from-[#9D4EDD]/10 to-[#FF6B9D]/10 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">{crystalRec.icon}</span>
            <div>
              <p className="text-xs uppercase tracking-wider text-[#9D4EDD]">Today's Weather Crystal</p>
              <p className="font-serif text-lg text-[#1e1919]">{crystalRec.crystal}</p>
            </div>
          </div>
          <p className="text-sm text-[#61525a]">{crystalRec.reason}</p>
        </div>
      )}
    </div>
  );
};

export default WeatherWidget;
